import { ComponentFactory, Injectable, ViewContainerRef, inject } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject } from "rxjs";
import { DrawerComponent, SliderConfiguration } from "../../core";
import { MessageService } from "./message.service";

@Injectable({
  providedIn: "root",
})
export class LoaderService {
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);

  /* #region Fields */
  private componentFactory: any = null;
  private rootViewContainer: ViewContainerRef;
  private readonly subject = new Subject<any>();
  private readonly subjectClose = new Subject<boolean>();
  private sliderConfig: SliderConfiguration = new SliderConfiguration();

  /* #region Public Methods */
  /** Method to subscrible to loader when component is added in side nav*/
  public onComponentAdd(): Observable<SliderConfiguration> {
    return this.subject.asObservable();
  }

  /** Method to subscribe to closing of component or form */
  public onFormClose(): Observable<boolean> {
    return this.subjectClose.asObservable();
  }

  /** Method to log an entry in console */
  public setRootViewContainerRef(viewContainerRef: ViewContainerRef): void {
    if(!this.rootViewContainer){
      this.rootViewContainer = viewContainerRef;
    }
  }

  /**This method is to check if the slider is already open and if so, shows pop-up */
  public addDynamicComponent(
    factory: ComponentFactory<DrawerComponent>,
    width: any,
    data: any,
    isReadOnly = false
  ): void {
    if (this.componentFactory === null) {
      this.createSliderComponent(factory, width, data, isReadOnly);
    } else {
      if (this.componentFactory.instance.isSliderDataEdited) {
        this.messageService
          .confirm(
            this.translateService.instant(
              "Common.GlobalMessages.UnSaveDataConfirmText"
            ),
            this.translateService.instant(
              "Common.GlobalMessages.UnSaveDataConfirmTitle"
            ),
            this.translateService.instant("Common.Button.Yes"),
            this.translateService.instant("Common.Button.No")
          )
          .subscribe((actionResult: any) => {
            if (actionResult == false) {
              this.cancelForm();
              this.createSliderComponent(factory, width, data, isReadOnly);
            } else if (actionResult == true) {
              this.subject.next(this.sliderConfig);
            } else {
              this.closePopUp();
            }
          });
      } else {
        this.createSliderComponent(factory, width, data, isReadOnly);
      }
    }
  }

  /** Method to Save form */
  public closeForm(): void {
    this.subjectClose.next(true);
    this.componentFactory.instance.isSliderDataEdited = false;
  }

  /** Method to cancel the form */
  public cancelForm(): void {
    this.subjectClose.next(false);
    this.componentFactory.instance.isSliderDataEdited = false;
  }

  /** Method to close confirmation pop up */
  public closePopUp(): void {
    this.componentFactory.instance.isSliderDataEdited = true;
  }

  /**This method is to check if the slider data is edited */
  public isSliderEdited(): boolean {
    if (this.componentFactory !== null) {
      return this.componentFactory.instance.isSliderDataEdited;
    }
    return false;
  }

  /**This method is used to open the on hold slider when there is an action on confirmation pop-up */
  public openSlider(): void {
    this.subject.next(this.sliderConfig);
  }
  /* #endregion */

  /* #region Private Methods */
  /**This method is to create new view on slider component */
  private createSliderComponent(
    factory: ComponentFactory<DrawerComponent>,
    width: any,
    data: any,
    isReadOnly: boolean
  ): void {
    this.rootViewContainer.clear();
    this.componentFactory = factory.create(this.rootViewContainer.injector);
    this.rootViewContainer.insert(this.componentFactory.hostView);
    (this.componentFactory.instance as DrawerComponent).sliderFormData = data;
    this.sliderConfig = { Width: width, IsSilderReadOnly: isReadOnly };
    this.componentFactory.instance.isSliderDataEdited = false;
    this.subject.next(this.sliderConfig);
  }
  /* #endregion */
}
